import React, { useState, useMemo, useEffect } from "react";
import { api } from "../../../api/service";

function InvoicesView() {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [bills, setBills] = useState([]);
  const [patientMap, setPatientMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [list, patientsList] = await Promise.all([
          api.getBills(0, 500),
          api.getPatients(0, 500).catch(() => []),
        ]);
        if (!cancelled) {
          if (Array.isArray(list)) setBills(list);
          if (Array.isArray(patientsList)) {
            const pMap = {};
            patientsList.forEach((p) => {
              if (p.uhid) pMap[p.uhid.toLowerCase()] = p.name || p.fullName || "";
            });
            setPatientMap(pMap);
          }
        }
      } catch {
        if (!cancelled) setBills([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const normalized = useMemo(() => {
    return bills.map((inv) => {
      const total = Number(inv.total) || 0;
      const insurance = Number(inv.insurance_covered) || 0;
      const paid = (inv.payments || []).reduce((s, p) => s + (Number(p.amount) || 0), 0);
      const due = Math.max(0, total - insurance - paid);
      const status = due <= 0 ? "Paid" : (inv.status || "Pending");
      const issueDate = (inv.created_at || "").slice(0, 10);
      // Resolve patient name: use stored name first, fallback to patientMap via uhid
      const uhidKey = (inv.uhid || "").toLowerCase();
      const resolvedName =
        inv.patient_name ||
        (uhidKey ? patientMap[uhidKey] : "") ||
        "—";
      return {
        id: inv.id || inv._id,
        invoiceNumber: inv.invoice_number || inv.id,
        patientName: resolvedName,
        uhid: inv.uhid || inv.patient_id || "—",
        issueDate: issueDate || "—",
        status,
        amount: total,
        raw: { ...inv, patient_name: resolvedName },
      };
    });
  }, [bills, patientMap]);

  const filtered = useMemo(() => {
    let list = normalized;
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (inv) =>
          String(inv.invoiceNumber).toLowerCase().includes(q) ||
          String(inv.patientName).toLowerCase().includes(q) ||
          String(inv.uhid).toLowerCase().includes(q)
      );
    }
    if (statusFilter) {
      list = list.filter((inv) => inv.status === statusFilter);
    }
    return list;
  }, [normalized, search, statusFilter]);

  const handlePrintInvoice = (inv) => {
    const printWindow = window.open("", "_blank", "width=900,height=800");
    if (!printWindow) {
      alert("Please allow popups to print the invoice.");
      return;
    }
    const itemsHtml = (inv.items || [])
      .map(
        (item, idx) => `
      <tr>
        <td style="padding: 10px; border-bottom: 1px solid #e2e8f0;">${idx + 1}</td>
        <td style="padding: 10px; border-bottom: 1px solid #e2e8f0;">${item.item_name || item.description || "Medical Service"}</td>
        <td style="padding: 10px; border-bottom: 1px solid #e2e8f0; text-align: center;">${item.quantity || 1}</td>
        <td style="padding: 10px; border-bottom: 1px solid #e2e8f0; text-align: right;">₹${(Number(item.price) || 0).toLocaleString()}</td>
        <td style="padding: 10px; border-bottom: 1px solid #e2e8f0; text-align: right;">₹${(Number(item.total) || (Number(item.price) || 0) * (Number(item.quantity) || 1)).toLocaleString()}</td>
      </tr>
    `
      )
      .join("");

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Invoice - ${inv.invoice_number || inv.id}</title>
          <style>
            @page { size: A4; margin: 15mm; }
            body { font-family: 'Segoe UI', Arial, sans-serif; color: #1e293b; margin: 0; padding: 0; font-size: 13px; }
            .header { text-align: center; border-bottom: 2px solid #0d9488; padding-bottom: 12px; margin-bottom: 20px; }
            .title { font-size: 22px; font-weight: 800; color: #134e4a; margin: 0; }
            .subtitle { font-size: 11px; color: #64748b; margin-top: 4px; }
            .meta-box { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; background: #f8fafc; padding: 14px; border-radius: 8px; border: 1px solid #e2e8f0; margin-bottom: 20px; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
            th { background: #f0fdfa; color: #0d9488; text-align: left; padding: 10px; font-size: 12px; border-bottom: 2px solid #0d9488; }
            .totals-box { margin-left: auto; width: 280px; font-size: 13px; }
            .totals-row { display: flex; justify-content: space-between; padding: 6px 0; border-bottom: 1px solid #f1f5f9; }
            .grand-total { font-size: 16px; font-weight: 800; color: #0d9488; border-top: 2px solid #0d9488; border-bottom: none; padding-top: 10px; margin-top: 6px; }
            .status-stamp { display: inline-block; padding: 6px 16px; border-radius: 6px; font-weight: 800; text-transform: uppercase; font-size: 12px; margin-top: 15px; }
            .paid { background: #dcfce7; color: #166534; border: 1px solid #bbf7d0; }
            .pending { background: #fef9c3; color: #854d0e; border: 1px solid #fef08a; }
          </style>
        </head>
        <body>
          <div class="header">
            <h2 class="title">SWASTIK HOSPITAL</h2>
            <div class="subtitle">Multi-Speciality Healthcare Center — Kolhapur, Maharashtra</div>
            <h3 style="margin: 10px 0 0; color: #0d9488; font-size: 14px; letter-spacing: 1px;">OFFICIAL MEDICAL INVOICE</h3>
          </div>

          <div class="meta-box">
            <div><strong>Patient Name:</strong> ${inv.patient_name || "—"}</div>
            <div><strong>Invoice #:</strong> ${inv.invoice_number || inv.id}</div>
            <div><strong>Patient UHID:</strong> ${inv.uhid || "—"}</div>
            <div><strong>Issue Date:</strong> ${(inv.created_at || "").slice(0, 10) || new Date().toLocaleDateString()}</div>
          </div>

          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Description</th>
                <th style="text-align: center;">Qty</th>
                <th style="text-align: right;">Unit Price</th>
                <th style="text-align: right;">Total</th>
              </tr>
            </thead>
            <tbody>
              ${itemsHtml || '<tr><td colspan="5" style="text-align:center; padding: 15px; color: #64748b;">Medical Service Charges</td></tr>'}
            </tbody>
          </table>

          <div class="totals-box">
            <div class="totals-row"><span>Subtotal:</span><span>₹${(Number(inv.subtotal) || Number(inv.total) || 0).toLocaleString()}</span></div>
            <div class="totals-row"><span>Insurance Covered:</span><span>₹${(Number(inv.insurance_covered) || 0).toLocaleString()}</span></div>
            <div class="totals-row grand-total"><span>Total Payable:</span><span>₹${(Number(inv.total) || 0).toLocaleString()}</span></div>
            <div class="status-stamp ${inv.status?.toLowerCase() === 'paid' ? 'paid' : 'pending'}">Status: ${inv.status || 'Pending'}</div>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 350);
  };

  return (
    <div className="billing-content">
      <div className="billing-card">
        <div className="billing-card__toolbar">
          <input
            type="text"
            className="billing-card__search"
            placeholder="Search by invoice #, patient, UHID..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select
            className="billing-card__filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All statuses</option>
            <option value="Paid">Paid</option>
            <option value="Pending">Pending</option>
            <option value="Overdue">Overdue</option>
            <option value="Draft">Draft</option>
          </select>
        </div>
        <div className="billing-table-wrap">
          {loading ? (
            <p className="billing-loading">Loading…</p>
          ) : (
          <table className="billing-table">
            <thead>
              <tr>
                <th>Invoice #</th>
                <th>Patient</th>
                <th>UHID</th>
                <th>Issue Date</th>
                <th>Status</th>
                <th>Amount</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={7} style={{ textAlign: "center", padding: "24px", color: "#64748b" }}>No invoices. Data from live database.</td></tr>
              ) : (
                filtered.map((inv) => (
                  <tr key={inv.id}>
                    <td>{inv.invoiceNumber}</td>
                    <td>{inv.patientName}</td>
                    <td>{inv.uhid}</td>
                    <td>{inv.issueDate}</td>
                    <td><span className={`status-badge status-badge--${inv.status.toLowerCase()}`}>{inv.status}</span></td>
                    <td>₹{Number(inv.amount).toLocaleString()}</td>
                    <td>
                      <button
                        type="button"
                        className="billing-btn billing-btn--sm billing-btn--secondary"
                        onClick={() => setSelectedInvoice(inv.raw)}
                        style={{ cursor: "pointer" }}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          )}
        </div>
      </div>

      {/* Invoice Details Modal */}
      {selectedInvoice && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(15, 23, 42, 0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            backdropFilter: "blur(4px)",
          }}
          onClick={() => setSelectedInvoice(null)}
        >
          <div
            style={{
              background: "white",
              borderRadius: "16px",
              maxWidth: "650px",
              width: "90%",
              maxHeight: "90vh",
              overflowY: "auto",
              boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)",
              padding: "2rem",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "2px solid #0d9488", paddingBottom: "1rem", marginBottom: "1.5rem" }}>
              <div>
                <h2 style={{ margin: 0, color: "#134e4a", fontSize: "1.4rem", fontWeight: 800, letterSpacing: "0.5px" }}>SWASTIK HOSPITAL</h2>
                <p style={{ margin: "2px 0 0", fontSize: "0.8rem", color: "#64748b" }}>Official Medical Invoice / Payment Slip</p>
              </div>
              <button
                onClick={() => setSelectedInvoice(null)}
                style={{ background: "none", border: "none", fontSize: "1.5rem", cursor: "pointer", color: "#64748b" }}
              >
                &times;
              </button>
            </div>

            {/* Meta Box */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem", background: "#f0fdfa", padding: "1rem", borderRadius: "10px", border: "1px solid #99f6e4", marginBottom: "1.5rem", fontSize: "0.88rem" }}>
              <div><strong>Invoice #:</strong> {selectedInvoice.invoice_number || selectedInvoice.id}</div>
              <div><strong>Date:</strong> {(selectedInvoice.created_at || "").slice(0, 10) || new Date().toLocaleDateString()}</div>
              <div><strong>Patient Name:</strong> {selectedInvoice.patient_name || "—"}</div>
              <div><strong>UHID:</strong> {selectedInvoice.uhid || "—"}</div>
              <div>
                <strong>Status:</strong>{" "}
                <span className={`status-badge status-badge--${(selectedInvoice.status || "pending").toLowerCase()}`}>
                  {selectedInvoice.status || "Pending"}
                </span>
              </div>
            </div>

            {/* Line Items */}
            <h3 style={{ fontSize: "1rem", color: "#134e4a", marginBottom: "0.75rem" }}>Services & Charges</h3>
            <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "1.5rem", fontSize: "0.88rem" }}>
              <thead>
                <tr style={{ background: "#f8fafc", borderBottom: "2px solid #cbd5e1", textAlign: "left" }}>
                  <th style={{ padding: "8px" }}>Item / Description</th>
                  <th style={{ padding: "8px", textAlign: "center" }}>Qty</th>
                  <th style={{ padding: "8px", textAlign: "right" }}>Price</th>
                  <th style={{ padding: "8px", textAlign: "right" }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {(selectedInvoice.items && selectedInvoice.items.length > 0) ? (
                  selectedInvoice.items.map((item, idx) => (
                    <tr key={idx} style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ padding: "8px" }}>{item.item_name || item.description || "Medical Service"}</td>
                      <td style={{ padding: "8px", textAlign: "center" }}>{item.quantity || 1}</td>
                      <td style={{ padding: "8px", textAlign: "right" }}>₹{(Number(item.price) || 0).toLocaleString()}</td>
                      <td style={{ padding: "8px", textAlign: "right" }}>₹{(Number(item.total) || (Number(item.price) || 0) * (Number(item.quantity) || 1)).toLocaleString()}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} style={{ padding: "12px", textAlign: "center", color: "#64748b" }}>
                      Standard Medical & Consultation Services
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* Totals Summary */}
            <div style={{ background: "#f8fafc", padding: "1rem", borderRadius: "10px", border: "1px solid #e2e8f0", marginBottom: "1.5rem" }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.4rem", fontSize: "0.9rem" }}>
                <span>Subtotal:</span>
                <span>₹{(Number(selectedInvoice.subtotal) || Number(selectedInvoice.total) || 0).toLocaleString()}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.4rem", fontSize: "0.9rem", color: "#059669" }}>
                <span>Insurance Covered:</span>
                <span>₹{(Number(selectedInvoice.insurance_covered) || 0).toLocaleString()}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", paddingTop: "0.5rem", borderTop: "2px solid #0d9488", fontWeight: 800, fontSize: "1.1rem", color: "#0d9488" }}>
                <span>Total Amount:</span>
                <span>₹{(Number(selectedInvoice.total) || 0).toLocaleString()}</span>
              </div>
            </div>

            {/* Actions */}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "1rem" }}>
              <button
                onClick={() => setSelectedInvoice(null)}
                className="billing-btn billing-btn--secondary"
                style={{ padding: "0.6rem 1.25rem", cursor: "pointer" }}
              >
                Close
              </button>
              <button
                onClick={() => handlePrintInvoice(selectedInvoice)}
                className="billing-btn billing-btn--primary"
                style={{ background: "#0d9488", color: "white", padding: "0.6rem 1.25rem", fontWeight: 700, cursor: "pointer" }}
              >
                🖨 Print Invoice
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default InvoicesView;
